import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { LoanFrequencyMultiplier } from "components/Application/constants";
import { T_Populate } from "types/_utils.types";
import { FileSchemaDto } from "dtos/file.dto";
import { FREQUENCY_TYPES, LoanSchemaDto } from "dtos/loan.dto";

export enum LoanIntelligencePriority {
  NORMAL = "Normal",
  NONE = "None",
  FIXED = "Fixed",
  HIGH = "High",
}

export enum AggressionLevel {
  LOW = "Low",
  MEDIUM = "Medium",
  HIGH = "High",
}

export const AGRESSION_LEVEL_VALUES: Record<AggressionLevel, number> = {
  [AggressionLevel.LOW]: 20,
  [AggressionLevel.MEDIUM]: 35,
  [AggressionLevel.HIGH]: 60,
};

export enum NegotiationStatus {
  SUCCESS = "success",
  ALTERNATE = "alternate",
  ROADBLOCK = "roadblock",
  NEGOTIATION_INITIATED = "negotiation_initiated",
  RENEGOTIATION = "renegotiation",
  MANUAL_ACCEPT = "manual_accept",
  MANUAL_OVERRIDE = "manual_override",
}

export enum NegotiateAs {
  CLIENT = "CLIENT",
  FCDS = "FCDS",
}

export type T_LoanIntelligenceLoan = Pick<LoanSchemaDto, "funded_amount" | "payback_amount" | "due_amount"> & {
  originalWeeklyPayment: number;
  priority: LoanIntelligencePriority;
  aggression?: AggressionLevel;
  suggestedWeeklyPayment?: number;
  startWeek?: number;
  collectedByCreditor?: boolean;
  originalDueAmount?: number;
  final_suggested?: number;
  final_negotiated?: number;
  notes?: string;
  negotiation_status?: NegotiationStatus;
  loan_id?: string;
  fixedWeightagePercent?: number;
  negotiate_as?: NegotiateAs;
  receivables?: number;
};

type T_LoanIntelligenceLoans = Record<string, T_LoanIntelligenceLoan>;

export type T_LoanIntelligence = {
  app_key: string;
  loans: {
    current: T_LoanIntelligenceLoans;
    original?: T_LoanIntelligenceLoans;
  };
  budget: {
    total: number;
    commissionPercentage: number;
    commissionAmount: number;
    negotiation: number;
  };
  creditorWeightage: Record<string, number>;
  uccScore?: number;
  calculationMode: "number" | "percentage";
  monthlyRevenue: number;
  percentageBudget?: number;
  percentageCommission?: number;
};

const initialData: T_LoanIntelligence = {
  app_key: "",
  budget: {
    total: 0,
    commissionPercentage: 22,
    commissionAmount: 0,
    negotiation: 0,
  },
  loans: {
    current: {},
  },
  creditorWeightage: {},
  calculationMode: "number",
  monthlyRevenue: 50000,
  percentageBudget: 10,
  percentageCommission: 10,
};

export const loanIntelligenceSlice = createSlice({
  name: "loanIntelligence",
  initialState: initialData,
  reducers: {
    loanIntelligenceInit: (
      state,
      {
        payload,
      }: PayloadAction<{
        app_key: string;
        loans: LoanSchemaDto[] | T_Populate<LoanSchemaDto, { attachments: FileSchemaDto[] }>[];
        totalWeeklyBudget: number;
        commissionPercentage: number;
        negotiationBudget: number;
        monthlyRevenue: number;
        calculationMode: "number" | "percentage";
      }>,
    ) => {
      state.app_key = payload.app_key;
      state.uccScore = undefined;
      state.loans.original = undefined;

      const loanInitial: T_LoanIntelligenceLoans = {};
      payload.loans.forEach((loan) => {
        console.log("Ran");
        console.log({ ree: loan.receivables });
        loanInitial[loan._id] = {
          funded_amount: loan.funded_amount,
          payback_amount: loan.payback_amount,
          due_amount: loan.due_amount,
          originalDueAmount: loan.due_amount,
          priority: LoanIntelligencePriority.NORMAL,
          originalWeeklyPayment:
            (loan.payments ?? 0) * (loan.frequency ? 5 / LoanFrequencyMultiplier[loan.frequency] : 0),
          negotiate_as: NegotiateAs.CLIENT,
          receivables: loan?.receivables,
        };
      });
      state.loans.current = loanInitial;
      state.budget.total = payload.totalWeeklyBudget;
      state.budget.commissionPercentage = payload.commissionPercentage;
      state.budget.commissionAmount = payload.totalWeeklyBudget - payload.negotiationBudget;
      state.budget.negotiation = payload.negotiationBudget;
      state.monthlyRevenue = payload.monthlyRevenue;
      state.calculationMode = payload.calculationMode;

      state.creditorWeightage =
        state.calculationMode === "percentage"
          ? calculateWeightageByReceivables(state.loans.current)
          : calculateWeightage(state.loans.current);

      calculateWeeklyPayment(state.loans.current, state.creditorWeightage, state.budget.negotiation);
    },
    loadSavedData: (state, { payload }: PayloadAction<T_LoanIntelligence>) => {
      state.app_key = payload.app_key;
      state.budget = payload.budget;
      state.creditorWeightage = payload.creditorWeightage;
      state.loans = payload.loans;
      state.uccScore = payload.uccScore;
      ((state.monthlyRevenue = payload.monthlyRevenue), (state.calculationMode = payload.calculationMode));
    },
    changeTotalWeeklyBudget: (state, { payload }: PayloadAction<number>) => {
      state.budget.total = payload;
      state.budget.negotiation = calculateNegotiationBudget(payload, state.budget.commissionAmount);

      calculateWeeklyPayment(state.loans.current, state.creditorWeightage, state.budget.negotiation);
    },
    // changeCommissionPercentage: (state, { payload }: PayloadAction<number>) => {
    //   state.budget.commissionPercentage = payload;
    //   state.budget.negotiation = calculateNegotiationBudget(state.budget.total, payload);

    //   calculateWeeklyPayment(state.loans.current, state.creditorWeightage, state.budget.negotiation);
    // },
    changeNegotiationBudget: (state, { payload }: PayloadAction<number>) => {
      state.budget.negotiation = payload;
      state.budget.commissionAmount = state.budget.total - payload;

      calculateWeeklyPayment(state.loans.current, state.creditorWeightage, state.budget.negotiation);
    },
    changeALoanBalance: (state, { payload }: PayloadAction<{ loanId: string; due_amount: number | undefined }>) => {
      state.loans.current[payload.loanId].due_amount = payload.due_amount;
      state.creditorWeightage = calculateWeightage(state.loans.current);
      calculateWeeklyPayment(state.loans.current, state.creditorWeightage, state.budget.negotiation);
    },
    changeAPaybackAmount: (
      state,
      { payload }: PayloadAction<{ loanId: string; payback_amount: number | undefined; alreadyPaidbackAmount: number }>,
    ) => {
      state.loans.current[payload.loanId].payback_amount = payload.payback_amount;
      state.loans.current[payload.loanId].due_amount = (payload.payback_amount ?? 0) - payload.alreadyPaidbackAmount;

      state.creditorWeightage = calculateWeightage(state.loans.current);
      calculateWeeklyPayment(state.loans.current, state.creditorWeightage, state.budget.negotiation);
    },
    changeAFundedAmount: (state, { payload }: PayloadAction<{ loanId: string; funded_amount: number | undefined }>) => {
      state.loans.current[payload.loanId].funded_amount = payload.funded_amount;
    },
    changeAOriginalDueAmount: (
      state,
      { payload }: PayloadAction<{ loanId: string; originalDueAmount: number | undefined }>,
    ) => {
      state.loans.current[payload.loanId].originalDueAmount = payload.originalDueAmount;
    },
    changeAPriority: (state, { payload }: PayloadAction<{ loanId: string; priority: LoanIntelligencePriority }>) => {
      state.loans.current[payload.loanId].priority = payload.priority;
      if (payload.priority === LoanIntelligencePriority.HIGH) {
        state.loans.current[payload.loanId].aggression = AggressionLevel.LOW;
      } else {
        state.loans.current[payload.loanId].aggression = undefined;
      }

      const freshNegotiationAmount = calculateNegotiationBudget(state.budget.total, state.budget.commissionAmount);
      state.creditorWeightage = calculateWeightage(state.loans.current);

      const { fixedAmount } = calculateWeeklyPayment(
        state.loans.current,
        state.creditorWeightage,
        freshNegotiationAmount,
      );
      state.budget.negotiation = freshNegotiationAmount - fixedAmount;
    },
    changeAPriorityFixedAmount: (
      state,
      {
        payload,
      }: PayloadAction<{
        loanId: string;
        fixedAmount: number | undefined;
        fixedPercent?: number; // new
      }>,
    ) => {
      state.loans.current[payload.loanId].suggestedWeeklyPayment = payload.fixedAmount;
      state.loans.current[payload.loanId].fixedWeightagePercent = payload.fixedPercent; // new
      const freshNegotiationAmount = calculateNegotiationBudget(state.budget.total, state.budget.commissionAmount);
      const { fixedAmount } = calculateWeeklyPayment(
        state.loans.current,
        state.creditorWeightage,
        freshNegotiationAmount,
      );
      state.budget.negotiation = freshNegotiationAmount - fixedAmount;
    },
    changeAnAggressionLevel: (state, { payload }: PayloadAction<{ loanId: string; aggression: AggressionLevel }>) => {
      state.loans.current[payload.loanId].aggression = payload.aggression;
      calculateWeeklyPayment(state.loans.current, state.creditorWeightage, state.budget.negotiation);
    },
    changeAStartWeek: (state, { payload }: PayloadAction<{ loanId: string; startWeek: number | undefined }>) => {
      state.loans.current[payload.loanId].startWeek = payload.startWeek;
    },
    changeASelfCollected: (state, { payload }: PayloadAction<{ loanId: string; selfCollected: boolean }>) => {
      state.loans.current[payload.loanId].collectedByCreditor = payload.selfCollected;
    },
    changeANegotiateAs: (state, { payload }: PayloadAction<{ loanId: string; negotiate_as: NegotiateAs }>) => {
      state.loans.current[payload.loanId].negotiate_as = payload.negotiate_as;
    },
    changeAFinalSuggested: (
      state,
      { payload }: PayloadAction<{ loanId: string; final_suggested: number | undefined }>,
    ) => {
      state.loans.current[payload.loanId].final_suggested = payload.final_suggested;
    },
    changeANotes: (state, { payload }: PayloadAction<{ loanId: string; notes: string | undefined }>) => {
      state.loans.current[payload.loanId].notes = payload.notes;
    },
    changeACreditorWeightage: (state, { payload }: PayloadAction<{ loanId: string; newWeightage: number }>) => {
      const currWeightagePrev = state.creditorWeightage[payload.loanId];
      // state.creditorWeightage[payload.loanId] = payload.newWeightage;

      const exceptCurrOneTotal = 100 - (currWeightagePrev ?? 0);
      const newWeightage: T_LoanIntelligence["creditorWeightage"] = {};
      Object.entries(state.creditorWeightage).forEach(([loanId, weightage]) => {
        if (loanId === payload.loanId) {
          newWeightage[loanId] = payload.newWeightage;
        } else {
          newWeightage[loanId] =
            state.creditorWeightage[loanId] -
            ((payload.newWeightage - (currWeightagePrev ?? 0)) * weightage) / exceptCurrOneTotal;
        }
      });

      state.creditorWeightage = newWeightage;
      calculateWeeklyPayment(state.loans.current, state.creditorWeightage, state.budget.negotiation);
    },
    changeUCCScore: (
      state,
      { payload }: PayloadAction<{ uccScore: number; loanOriginal: T_LoanIntelligenceLoans }>,
    ) => {
      state.uccScore = payload.uccScore;
      state.loans.original = payload.loanOriginal;

      const uccReduction = calculateUCCReduction(payload.uccScore);

      Object.entries(state.loans.current).forEach(([loanId, loan]) => {
        const original = payload.loanOriginal[loanId];
        const modified = calculateUCCReducedAmount(uccReduction, original);

        loan.funded_amount = modified.funded_amount;
        loan.payback_amount = modified.payback_amount;
        loan.due_amount = modified.due_amount;
      });

      state.creditorWeightage = calculateWeightage(state.loans.current);
      calculateWeeklyPayment(state.loans.current, state.creditorWeightage, state.budget.negotiation);
    },
    removeUCCScore: (state, { payload }: PayloadAction<{ loanOriginal: T_LoanIntelligenceLoans }>) => {
      state.uccScore = undefined;
      state.loans.original = undefined;
      state.loans.current = structuredClone(payload.loanOriginal);

      state.creditorWeightage = calculateWeightage(state.loans.current);
      calculateWeeklyPayment(state.loans.current, state.creditorWeightage, state.budget.negotiation);
    },
    applyAiIntelAggression: (
      state,
      {
        payload,
      }: PayloadAction<{
        aiIntel: {
          [k: string]: {
            min: number | undefined;
            max: number | undefined;
            risk_factor: 0 | 1 | 2 | 3 | undefined;
          };
        };
      }>,
    ) => {
      Object.entries(state.loans.current).forEach(([loanId, loan]) => {
        const foundIntel = payload.aiIntel?.[loanId];
        if (
          foundIntel &&
          typeof foundIntel.risk_factor === "number" &&
          foundIntel.risk_factor !== 0 &&
          foundIntel.risk_factor <= 3
        ) {
          loan.priority = LoanIntelligencePriority.HIGH;
          loan.aggression = {
            1: AggressionLevel.LOW,
            2: AggressionLevel.MEDIUM,
            3: AggressionLevel.HIGH,
          }[foundIntel.risk_factor];
        }
      });

      calculateWeeklyPayment(state.loans.current, state.creditorWeightage, state.budget.negotiation);
    },
    toggleCalculationMode: (state) => {
      const newMode = state.calculationMode === "number" ? "percentage" : "number";
      state.calculationMode = newMode;

      if (newMode === "percentage") {
        // Convert from number to percentage mode
        // const weeklyRevenue = state.monthlyRevenue / 4;
        // state.percentageBudget = (state.budget.total / weeklyRevenue) * 100;
        // console.log({ percentageBudget: state.percentageBudget });
        // // Store commission as percentage for display
        // state.percentageCommission = state.budget.commissionPercentage;
        // console.log({ percentageCommission: state.percentageCommission });
        const weeklyRevenue = state.monthlyRevenue / 4;
        state.budget.total = (weeklyRevenue * (state.percentageBudget ?? 0)) / 100;
        state.budget.commissionAmount = (state.budget.total * state.budget.commissionPercentage) / 100;
        state.budget.negotiation = state.budget.total - state.budget.commissionAmount;
        state.creditorWeightage = calculateWeightageByReceivables(state.loans.current);
        calculateWeeklyPayment(state.loans.current, state.creditorWeightage, state.budget.negotiation);
      } else {
        // Convert from percentage to number mode
        state.creditorWeightage = calculateWeightage(state.loans.current);
        state.percentageBudget = undefined;
        state.percentageCommission = undefined;
      }
    },
    setMonthlyRevenue: (state, { payload }: PayloadAction<number>) => {
      state.monthlyRevenue = payload;

      // Recalculate if in percentage mode
      if (state.calculationMode === "percentage" && state.percentageBudget) {
        const weeklyRevenue = payload / 4;
        state.budget.total = (weeklyRevenue * (state.percentageBudget ?? 0)) / 100;
        state.budget.commissionAmount = (state.budget.total * state.budget.commissionPercentage) / 100;
        state.budget.negotiation = state.budget.total - state.budget.commissionAmount;
        calculateWeeklyPayment(state.loans.current, state.creditorWeightage, state.budget.negotiation);
      }
    },
    setPercentageBudget: (state, { payload }: PayloadAction<number>) => {
      state.percentageBudget = payload;

      if (state.calculationMode === "percentage") {
        const weeklyRevenue = state.monthlyRevenue / 4;
        state.budget.total = (weeklyRevenue * payload) / 100;
        // Recalculate commission amount with current commission percentage
        state.budget.commissionAmount = (state.budget.total * state.budget.commissionPercentage) / 100;
        state.budget.negotiation = state.budget.total - state.budget.commissionAmount;
        calculateWeeklyPayment(state.loans.current, state.creditorWeightage, state.budget.negotiation);
      }
    },
    setPercentageCommission: (state, { payload }: PayloadAction<number>) => {
      state.percentageCommission = payload;

      if (state.calculationMode === "percentage") {
        // payload is the commission percentage (e.g., 22 for 22%)
        //agency commision will be deducted from from weekly budget i.e from monthkly revenue/4
        state.budget.commissionPercentage = payload;
        state.budget.commissionAmount = (state.budget.total * payload) / 100;
        state.budget.negotiation = state.budget.total - state.budget.commissionAmount;
        calculateWeeklyPayment(state.loans.current, state.creditorWeightage, state.budget.negotiation);
      }
    },
  },
});

function calculateCurrentBalance(loans: T_LoanIntelligenceLoans | { due_amount?: number }[]): number {
  if (Array.isArray(loans)) {
    return loans.reduce((prev, curr) => {
      return prev + (curr.due_amount ?? 0);
    }, 0);
  }
  return Object.values(loans).reduce((prev, curr) => {
    return prev + (curr.due_amount ?? 0);
  }, 0);
}

function calculateWeightage(loans: T_LoanIntelligenceLoans): T_LoanIntelligence["creditorWeightage"] {
  const weightageObj: T_LoanIntelligence["creditorWeightage"] = {};
  const priorityNormalHighLoans = Object.entries(loans).filter(([loanId, loan]) => {
    if (loan.priority === LoanIntelligencePriority.NORMAL || loan.priority === LoanIntelligencePriority.HIGH)
      return true;
  });
  const priorityNormalHighCurrentBalance = calculateCurrentBalance(priorityNormalHighLoans.map((el) => el?.[1]));
  priorityNormalHighLoans.forEach(([loanId, loan]) => {
    weightageObj[loanId] = ((loan.due_amount ?? 0) / priorityNormalHighCurrentBalance) * 100;
  });
  return weightageObj;
}
function calculateWeightageByReceivables(loans: T_LoanIntelligenceLoans): T_LoanIntelligence["creditorWeightage"] {
  const weightageObj: T_LoanIntelligence["creditorWeightage"] = {};

  const priorityNormalHighLoans = Object.entries(loans).filter(
    ([_, loan]) => loan.priority === LoanIntelligencePriority.NORMAL || loan.priority === LoanIntelligencePriority.HIGH,
  );
  console.log("this");

  const totalReceivables = priorityNormalHighLoans.reduce((sum, [_, loan]) => sum + (loan.receivables ?? 0), 0);

  priorityNormalHighLoans.forEach(([loanId, loan]) => {
    weightageObj[loanId] = totalReceivables > 0 ? ((loan.receivables ?? 0) / totalReceivables) * 100 : 0;
  });

  return weightageObj;
}

export function calculateNegotiationBudget(total: number, commissionAmount: number) {
  // return total * ((100 - commissionPercentage) / 100);
  return total - commissionAmount;
}

function calculateWeeklyPayment(
  loans: T_LoanIntelligenceLoans,
  creditorWeightage: T_LoanIntelligence["creditorWeightage"],
  negotiationBudget: number,
) {
  let fixedAmount = 0;
  let modifiedNegotiationBudget = negotiationBudget;
  Object.entries(loans).forEach(([loanId, loan]) => {
    if (loan.priority === LoanIntelligencePriority.NONE) {
      loan.suggestedWeeklyPayment = undefined;
    } else if (loan.priority === LoanIntelligencePriority.FIXED) {
      loan.suggestedWeeklyPayment = loan?.suggestedWeeklyPayment ?? 0;
      fixedAmount += loan.suggestedWeeklyPayment ?? 0;
    }
  });

  modifiedNegotiationBudget = modifiedNegotiationBudget - fixedAmount;
  let priorityHighAmount = 0;
  Object.entries(loans).forEach(([loanId, loan]) => {
    if (loan.priority === LoanIntelligencePriority.HIGH) {
      const newSuggestedWeeklyPayment = calculateAggressiveWeeklyPayment({
        loanId: loanId,
        aggression: loan.aggression,
        creditorWeightage: creditorWeightage,
        negotiationBudget: modifiedNegotiationBudget,
        loans: loans,
      });
      loan.suggestedWeeklyPayment = newSuggestedWeeklyPayment;
      priorityHighAmount += newSuggestedWeeklyPayment;
    }
  });

  modifiedNegotiationBudget = modifiedNegotiationBudget - priorityHighAmount;
  const priorityNormalCreditorWeightage = Object.entries(creditorWeightage)
    .filter(([loanId]) => {
      if (loans[loanId].priority === LoanIntelligencePriority.NORMAL) return true;
      else return false;
    })
    .reduce((prev, curr) => {
      return prev + curr[1];
    }, 0);

  Object.entries(loans).forEach(([loanId, loan]) => {
    if (loan.priority === LoanIntelligencePriority.NORMAL) {
      loan.suggestedWeeklyPayment =
        modifiedNegotiationBudget * (creditorWeightage[loanId] / priorityNormalCreditorWeightage);
    }
  });
  return { fixedAmount };
}

function calculateAggressiveWeeklyPayment({
  loanId,
  aggression,
  negotiationBudget,
  creditorWeightage,
  loans,
}: {
  loanId: string;
  aggression: AggressionLevel | undefined;
  negotiationBudget: number;
  creditorWeightage: T_LoanIntelligence["creditorWeightage"];
  loans: T_LoanIntelligenceLoans;
}) {
  /* 1. Get suggested & original weekly payment |difference| (in mod) for all all priority Normal & High loans */
  const diffs: Record<string, number> = {};
  Object.entries(loans).forEach(([_loanId, loan]) => {
    if (loan.priority === LoanIntelligencePriority.NORMAL || loan.priority === LoanIntelligencePriority.HIGH) {
      diffs[_loanId] = Math.abs(
        (loan.originalWeeklyPayment ?? 0) - (negotiationBudget * creditorWeightage[_loanId]) / 100,
      );
    }
  });

  /* 2. Prepare diff total */
  const diffTotal = Object.values(diffs).reduce((prev, curr) => prev + curr);

  /**                                                 current loan diff
   * 3. For the current loan, Δ = -----------------------------------------------------------
   *                                 diff total x (negotiation budget - current loan diff)
   **/
  const delta = (diffs[loanId] / diffTotal) * (negotiationBudget - diffs[loanId]);
  const normalSuggested = negotiationBudget * (creditorWeightage[loanId] / 100);

  const newSuggestedWeeklyPayment =
    normalSuggested + (delta * AGRESSION_LEVEL_VALUES[aggression ?? AggressionLevel.LOW]) / 100;

  return newSuggestedWeeklyPayment;
}

type T_RiskLevel = {
  range: [number, number];
  rating: string;
  principal: number;
  interest: number;
};
export const RISK_LEVELS: T_RiskLevel[] = [
  {
    range: [0, 10],
    rating: "Very good",
    principal: 0,
    interest: 80,
  },
  {
    range: [10, 25],
    rating: "Good",
    principal: 0,
    interest: 40,
  },
  {
    range: [25, 40],
    rating: "OK",
    principal: 0,
    interest: 30,
  },
  {
    range: [40, 60],
    rating: "Low risk",
    principal: 0,
    interest: 20,
  },
  {
    range: [60, 85],
    rating: "Moderate risk",
    principal: 0,
    interest: 10,
  },
  {
    range: [85, 100],
    rating: "High risk",
    principal: 0,
    interest: 0,
  },
];

function calculateUCCReduction(uccScrore: number): { principal: number; interest: number } {
  // if (uccScrore >= 0 && uccScrore <= 10) {
  //   return { principal: 10, interest: 80 };
  // } else if (uccScrore > 10 && uccScrore <= 25) {
  //   return { principal: 5, interest: 40 };
  // } else if (uccScrore > 25 && uccScrore <= 40) {
  //   return { principal: 0, interest: 30 };
  // } else if (uccScrore > 40 && uccScrore <= 60) {
  //   return { principal: 0, interest: 20 };
  // } else if (uccScrore > 60 && uccScrore <= 85) {
  //   return { principal: 0, interest: 10 };
  // } else {
  //   return { principal: 0, interest: 0 };
  // }
  let foundRange: T_RiskLevel | undefined = undefined;
  if (uccScrore === 0) {
    return (foundRange = RISK_LEVELS[0]);
  }
  foundRange = RISK_LEVELS.find((el) => uccScrore > el.range[0] && uccScrore <= el.range[1]);
  if (foundRange) {
    return { principal: foundRange.principal, interest: foundRange.interest };
  } else {
    return { principal: 0, interest: 0 };
  }
}

function calculateUCCReducedAmount(
  uccReduction: ReturnType<typeof calculateUCCReduction>,
  original: T_LoanIntelligenceLoan,
) {
  let interest = (original?.payback_amount ?? 0) - (original?.funded_amount ?? 0);
  const alreadyPaidbackAmount = (original?.payback_amount ?? 0) - (original?.due_amount ?? 0);

  const funded_amount = ((original?.funded_amount ?? 0) * (100 - uccReduction.principal)) / 100;

  interest = (interest * (100 - uccReduction.interest)) / 100;
  const payback_amount = funded_amount + interest;
  const due_amount = payback_amount - alreadyPaidbackAmount;

  return { funded_amount, payback_amount, due_amount };
}

export const {
  loanIntelligenceInit,
  loadSavedData,
  changeALoanBalance,
  changeAPaybackAmount,
  changeAFundedAmount,
  changeAOriginalDueAmount,
  changeAPriorityFixedAmount,
  changeAnAggressionLevel,
  // changeCommissionPercentage,
  changeNegotiationBudget,
  changeTotalWeeklyBudget,
  changeAPriority,
  changeAStartWeek,
  changeASelfCollected,
  changeANegotiateAs,
  changeAFinalSuggested,
  changeANotes,
  changeACreditorWeightage,
  changeUCCScore,
  removeUCCScore,
  applyAiIntelAggression,
  toggleCalculationMode,
  setMonthlyRevenue,
  setPercentageBudget,
  setPercentageCommission,
} = loanIntelligenceSlice.actions;
